import mitraEnrollmentApi from '@/api/mitra-enrollment/mitra-enrollment';
import { normalizeErrorMessage } from '@/utils/error-message';
import { normalizeBatchList } from '@/utils/batchIdentity';
import { getRemainingQuota, isQuotaFull } from '@/utils/enrollmentQuota';

const isDraftParticipant = participant => !participant?.status && !participant?.submitted;
const matchesParticipant = (left, right) => {
  if (left?.nik && right?.nik) {
    return String(left.nik) === String(right.nik);
  }
  return left?.tna_id != null && right?.tna_id != null &&
    String(left.tna_id) === String(right.tna_id);
};

const adjustSuggestedCount = (batch, difference) => {
  ['enrolled', 'enrolled_count'].forEach((key) => {
    const value = Number(batch[key]);
    if (Number.isFinite(value)) batch[key] = Math.max(0, value + difference);
  });
};

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Batch related state
  batches: [],
  availableParticipants: [], // Shared participants from API
  batchLoading: false,
  batchError: null,
  // Summary statistics
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  summaryLoading: false
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Batch getters
  batches: state => state.batches || [],
  availableParticipants: state => state.availableParticipants || [],
  batchLoading: state => state.batchLoading || false,
  batchError: state => state.batchError || null,
  // Summary getters
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  summaryLoading: state => state.summaryLoading || false
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Batch mutations
  SET_BATCHES(state, batches) {
    state.batches = Array.isArray(batches)
      ? batches.map(batch => ({
        ...batch,
        // BE adds draft recommendations to `enrolled` after calculating quota flags.
        enrolled_count: Number(batch.enrolled ?? batch.enrolled_count ?? 0),
        company_participants: batch.company_participants || []
      }))
      : [];
  },
  SET_AVAILABLE_PARTICIPANTS(state, participants) {
    state.availableParticipants = Array.isArray(participants) ? participants : [];
  },
  SET_BATCH_LOADING(state, loading) {
    state.batchLoading = loading;
  },
  SET_BATCH_ERROR(state, error) {
    state.batchError = error;
  },
  CLEAR_BATCH_ERROR(state) {
    state.batchError = null;
  },
  // Move a draft recommendation, or add an unassigned participant, in one step.
  ADD_PARTICIPANT_TO_BATCH(state, { batchIndex, participant, sourceBatchIndex }) {
    const target = state.batches[batchIndex];
    if (sourceBatchIndex != null) {
      const source = state.batches[sourceBatchIndex];
      const sourceParticipantIndex = source.company_participants.findIndex(item => matchesParticipant(item, participant));
      source.company_participants.splice(sourceParticipantIndex, 1);
      adjustSuggestedCount(source, -1);
    }
    target.company_participants.push({ ...participant, status: null, submitted: false });
    adjustSuggestedCount(target, 1);
  },
  // Remove participant from batch (local state)
  REMOVE_PARTICIPANT_FROM_BATCH(state, { batchIndex, participantNik }) {
    if (state.batches[batchIndex]) {
      const index = state.batches[batchIndex].company_participants.findIndex(p => p.nik === participantNik);
      if (index !== -1) {
        // Get participant data before removing
        const removedParticipant = state.batches[batchIndex].company_participants[index];
        
        // Remove from batch
        state.batches[batchIndex].company_participants.splice(index, 1);
        adjustSuggestedCount(state.batches[batchIndex], -1);
        
        // Add back to availableParticipants if not already there
        const existsInAvailable = state.availableParticipants.some(p => p.nik === participantNik);
        if (!existsInAvailable && removedParticipant) {
          state.availableParticipants.push(removedParticipant);
        }
      }
    }
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    if (summary) {
      state.summary = {
        this_year: summary.this_year || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
        history: summary.history || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
      };
    }
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  }
};

const actions = {
  async getCourses({ commit }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraEnrollmentApi.getCourses(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data);
        commit('SET_PAGINATION', {
          total: response.data.data.total || 0,
          page: response.data.data.page || 1,
          limit: response.data.data.limit || 10,
          totalPage: response.data.data.totalPage || 0
        });
        return response.data.data.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat daftar pelatihan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat daftar pelatihan.');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraEnrollmentApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat detail pelatihan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat detail pelatihan.');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get batches with participants
  async getBatches({ commit }, { courseId, params = {} }) {
    commit('SET_BATCH_LOADING', true);
    commit('CLEAR_BATCH_ERROR');
    
    try {
      const response = await mitraEnrollmentApi.getBatches(courseId, params);
      
      if (response.data.httpCode === 200) {
        const payload = response.data.data;
        commit('SET_BATCHES', normalizeBatchList(payload));
        commit('SET_AVAILABLE_PARTICIPANTS', payload?.participantsToSubmit || []);
        return payload;
      } else {
        throw new Error(response.data.message || 'Gagal memuat data batch pelatihan.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal memuat data batch pelatihan.');
      commit('SET_BATCH_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_BATCH_LOADING', false);
    }
  },

  // Submit participants (final submit)
  async submitParticipants({ commit }, { batchId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraEnrollmentApi.submitParticipants(batchId, data);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal menyimpan peserta.');
      }
    } catch (error) {
      const errorMessage = normalizeErrorMessage(error, 'Gagal menyimpan peserta.');
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get summary statistics
  async getSummary({ commit }, params) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await mitraEnrollmentApi.getSummary(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Gagal memuat ringkasan enrollment.');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  },

  // Local state management
  addParticipantToBatch({ state, commit }, { batchIndex, participant }) {
    const target = state.batches[batchIndex];
    if (!target) throw new Error('Batch tujuan tidak ditemukan. Muat ulang daftar batch.');
    const assignments = state.batches.flatMap((batch, index) =>
      (batch.company_participants || [])
        .filter(item => matchesParticipant(item, participant))
        .map(item => ({ batch, index, item }))
    );
    if (assignments.some(({ item }) => !isDraftParticipant(item))) {
      throw new Error('Peserta sudah masuk proses enrollment dan tidak dapat dipindahkan.');
    }
    if (assignments.some(({ index }) => index === batchIndex)) {
      throw new Error('Peserta sudah berada di batch ini.');
    }
    const remaining = getRemainingQuota(target);
    const pending = (target.company_participants || []).filter(isDraftParticipant).length;
    if (isQuotaFull(target) || (remaining != null && pending >= remaining)) {
      throw new Error('Kuota batch tujuan sudah penuh. Pilih batch lain.');
    }
    const source = assignments[0];
    commit('ADD_PARTICIPANT_TO_BATCH', {
      batchIndex,
      participant: source?.item || participant,
      sourceBatchIndex: source?.index
    });
    return { movedFrom: source?.batch || null };
  },

  removeParticipantFromBatch({ commit }, payload) {
    commit('REMOVE_PARTICIPANT_FROM_BATCH', payload);
  },

  // Download enrollment template
  async downloadEnrollmentTemplate(_, { courseId, params = {} }) {
    try {
      const response = await mitraEnrollmentApi.downloadTemplate(courseId, params);
      return response;
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  },

  async downloadGlobalEnrollmentTemplate(_, params = {}) {
    try {
      const response = await mitraEnrollmentApi.downloadGlobalTemplate(params);
      return response;
    } catch (error) {
      console.error('Error downloading global template:', error);
      throw error;
    }
  },

  // Bulk upload enrollment
  async bulkUploadEnrollment(_, { courseId, formData }) {
    try {
      const response = await mitraEnrollmentApi.uploadEnrollment(courseId, formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading enrollment:', error);
      throw error;
    }
  },

  async bulkUploadGlobalEnrollment(_, formData) {
    try {
      const response = await mitraEnrollmentApi.uploadGlobalEnrollment(formData);
      return response.data;
    } catch (error) {
      console.error('Error uploading global enrollment:', error);
      throw error;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
